import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { BusinessunitModel } from 'projects/core/src/lib/models/businessunit.model';
import { JobChangeEvent, PlannerOrderformJobinformationComponent } from './partials/orderform-jobinformation/orderform-jobinformation.component';
import { PlannerOrderformCandidateComponent } from './partials/orderform-candidate/orderform-candidate.component';
import { PlannerOrderformDateComponent } from './partials/orderform-date/orderform-date.component';
import { PlannerOrderformDocumentsComponent } from './partials/orderform-documents/orderform-documents.component';
import { PlannerOrderformParticipantsComponent } from './partials/orderform-participants/orderform-participants.component';
import { PlannerOrderformTemplateComponent, TemplateChangeEvent } from './partials/orderform-template/orderform-template.component';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { OwnObserversChangeEvent } from './partials/orderform-own-observers/orderform-own-observers.component';
import { switchMap } from 'rxjs/operators';
import { ParticipantData } from '../../partials/participant-form/participant-form.component';
import { OrderHelperService } from '../../../services/order-helper.service';
import { AuthStateService } from '../../../services/auth-state.service';
import { HelperService } from '../../../services/helper.service';

@Component({
  selector: 'app-orderform',
  templateUrl: './orderform.component.html',
  styleUrls: ['./orderform.component.scss']
})
export class PlannerOrderformComponent implements OnInit {

  @ViewChild('jobinformation') jobinformation: PlannerOrderformJobinformationComponent;
  @ViewChild('template') template: PlannerOrderformTemplateComponent;
  @ViewChild('date') date: PlannerOrderformDateComponent;
  @ViewChild('candidate') candidate: PlannerOrderformCandidateComponent;
  @ViewChild('participants') participants: PlannerOrderformParticipantsComponent;
  @ViewChild('ownobservers') ownobservers: PlannerOrderformParticipantsComponent;
  @ViewChild('documents') documents: PlannerOrderformDocumentsComponent;

  public loading = true;
  public submitting = false;

  public form: UntypedFormGroup = this.fb.group({});
  public options: { [key: string]: Array<SelectItem | SelectItemGroup> } = {};
  public templates: Array<TemplateModel> = [];
  public filteredTemplates: Array<SelectItem> = [];
  public texts: { [key: string]: string } = {};
  public user: ParticipantData = null;

  public needsJobDescription = false;

  private selectedBusinessunit: BusinessunitModel = null;
  private selectedTemplate: TemplateModel = null;
  private hasJob = true;

  constructor(
    private api: ApiService,
    private router: Router,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private transloco: TranslocoService,
    private orderhelper: OrderHelperService,
    private helper: HelperService,
    public auth: AuthStateService
  ) { }

  ngOnInit(): void {
    this.api.getOrderformSetupData().subscribe({
      next: (result: any) => {
        if (result.data?.options) {
          this.options = this.orderhelper.getMappedOptions(result.data?.options);
          this.texts = this.orderhelper.getMmappedTexts(result.data?.texts);
          this.templates = this.orderhelper.getTemplates(result.data?.templates);
          this.user = result.data?.user;
        }
      },
      error: (error: any) => {
      }
    }).add(() => {
      this.loading = false;
    });
  }

  public onCancel(): void {
    this.router.navigate(['/planning/orders']);
  }

  public onSubmit(): void {
    this.ui.hideMessage();
    this.date.setSubmitted();
    this.documents.hasJobError = false;
    const payload = this.getPayload();
    const jobError = this.isJobOkay() === false;
    if (!jobError && this.form.valid) {
      let orderId = null;
      this.submitting = true;
      this.api.createNewOrder(payload)
        .pipe(
          switchMap((result: any) => {
            orderId = result.data?.id;
            return this.api.uploadFiles(this.documents.getFiles(), 'ac.file', result.data?.id, this.documents.getPayload());
          })
        )
        .subscribe({
          next: (result: any) => {
            this.ui.toastSuccess(this.transloco.translate('common.messages.order_created'));
            if (this.auth.hasRights === true) {
              this.router.navigate(['/planning/orders']);
            } else {
              this.router.navigateByUrl('/planning/confirmation', { state: { id: orderId } });
            }
          },
          error: (error: any) => {
            this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
            FormHelper.setBackendErrors(this.form, error.fielderrors);
          },
        })
        .add(() => {
          this.submitting = false;
        });
    } else {
      this.ui.showMessage('error', this.transloco.translate('backend.exceptions.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
      if (jobError === true) {
        this.documents.hasJobError = true;
      }
    }
  }

  public jobChanged(event: JobChangeEvent | null): void {
    if (event !== null) {
      this.hasJob = event.hasjob;
      this.filteredTemplates = this.templates.filter((template: TemplateModel) => {
        // filter with candidatetype
        return template.settings?.candidatetype === 'both' || template.settings?.candidatetype === event.candidatetype;
      }).filter((template: TemplateModel) => {
        // filter with businessunit
        const businessunit = template.businessunits.find((businessunit: BusinessunitModel) => {
          return businessunit.id === event.businessunit.id;
        });
        return businessunit !== undefined && businessunit.pivot?.active === true;
      }).map((template: TemplateModel) => {
        // map to selectitem
        return {
          value: template,
          label: template.name_language
        };
      });
      this.selectedBusinessunit = event.businessunit;
      if (this.date) {
        this.date.setBusinessunit(event.businessunit);
      }
      this.processSettings();
    }
  }

  public templateChanged(event: TemplateChangeEvent | null): void {
    if (this.date) {
      this.date.setTemplate(event);
    }
    if (event !== null) {
      this.selectedTemplate = event.template;
      this.processSettings();
    }
  }

  public ownObserversChanged(event: OwnObserversChangeEvent): void {
    if (this.date) {
      this.date.setObserverMode(event);
    }
  }

  private processSettings(): void {
    if (this.selectedBusinessunit !== null && this.selectedTemplate !== null) {
      const templateSettings = this.selectedTemplate.settings;
      const crossSettings = this.selectedTemplate.businessunits.find((businessunit: BusinessunitModel) => {
        return businessunit.id === this.selectedBusinessunit.id;
      })?.pivot?.settings;
      this.template.setSettings(templateSettings, crossSettings);
      this.ownobservers.setSettings(templateSettings, crossSettings);
      this.participants.setSettings(templateSettings, crossSettings);
      this.candidate.setSettings(templateSettings, crossSettings);
      this.needsJobDescription = this.hasJob === true && crossSettings?.job === true;
    }
  }

  private getPayload(): any {
    return {
      jobinformation: this.jobinformation.getPayload(),
      template: this.template.getPayload(),
      ownobservers: this.ownobservers.getPayload(),
      date: this.date.getPayload(),
      candidate: this.candidate.getPayload(),
      participants: this.participants.getPayload(),
      documents: this.documents.getPayload(),
    };
  }

  private isJobOkay(): boolean {
    if (this.needsJobDescription === true) {
      const payload = this.documents.getPayload();
      let found = false;
      Object.keys(payload).forEach((key: string) => {
        if (payload[key].type === 'job_description') {
          found = true;
        }
      });
      return found;
    }
    return true;
  }

}
